import { Organization, UserRole } from '../types';
import { isAdminRole } from '../utils/roles';

export type AppModule =
  | 'dashboard'
  | 'tasks'
  | 'tickets'
  | 'vehicles'
  | 'drivers'
  | 'hires'
  | 'driverInvoices'
  | 'settings'
  | 'reports'
  | 'billing'
  | 'pricing';

interface ResolveModuleAccessInput {
  organization: Organization | null;
  role: UserRole;
}

export interface ModuleAccessResult {
  allowedModules: AppModule[];
  blockedRoutes: string[];
  isFleetMode: boolean;
  canAccessModule: (module: AppModule) => boolean;
  isRouteBlocked: (path: string) => boolean;
}

const CORE_MODULES: AppModule[] = [
  'dashboard',
  'tasks',
  'tickets',
  'vehicles',
  'drivers',
  'settings'
];

const HIRE_MODULES: AppModule[] = ['hires', 'driverInvoices'];
const ADMIN_MODULES: AppModule[] = ['reports', 'billing', 'pricing'];

const MODULE_ROUTES: Record<AppModule, string[]> = {
  dashboard: ['/', '/dashboard'],
  tasks: ['/tasks'],
  tickets: ['/tickets'],
  vehicles: ['/vehicles'],
  drivers: ['/drivers'],
  hires: ['/hires'],
  driverInvoices: ['/driver-invoices'],
  settings: ['/settings'],
  reports: ['/reports'],
  billing: ['/billing'],
  pricing: ['/pricing']
};

const normalizePath = (path: string) => {
  const raw = String(path || '').trim();
  if (!raw) return '/';
  const [pathname] = raw.split(/[?#]/);
  return pathname || '/';
};

export const getModuleForPath = (path: string): AppModule | null => {
  const pathname = normalizePath(path);
  const entries = Object.entries(MODULE_ROUTES) as Array<[AppModule, string[]]>;
  for (const [module, prefixes] of entries) {
    if (prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
      return module;
    }
  }
  return null;
};

export const getBlockedModuleToastMessage = () =>
  'Module unavailable in current fleet mode. Enable hire workflows in Settings if needed.';

export const resolveModuleAccess = ({
  organization,
  role
}: ResolveModuleAccessInput): ModuleAccessResult => {
  const isFleetMode = organization?.business_model === 'fleet_operator';
  const hireWorkflowsEnabled = !isFleetMode || organization?.enable_hire_workflows === true;

  const allowed = new Set<AppModule>(CORE_MODULES);
  if (hireWorkflowsEnabled) {
    HIRE_MODULES.forEach((module) => allowed.add(module));
  }
  if (isAdminRole(role)) {
    ADMIN_MODULES.forEach((module) => allowed.add(module));
  }

  const blockedRoutes = (Object.entries(MODULE_ROUTES) as Array<[AppModule, string[]]>)
    .filter(([module]) => !allowed.has(module))
    .flatMap(([, routes]) => routes.filter((route) => route !== '/'));

  return {
    allowedModules: Array.from(allowed),
    blockedRoutes,
    isFleetMode,
    canAccessModule: (module: AppModule) => allowed.has(module),
    isRouteBlocked: (path: string) => {
      const module = getModuleForPath(path);
      return module ? !allowed.has(module) : false;
    }
  };
};

